import React, { useState } from "react";
import { Button, Modal } from "antd";
import {
    LicenseKey,
    LicenseState,
    useSubscriptionLicenseKeys,
} from "services/rest/subscription";
import { StoreTable } from "toolkit/table/StoreTable";
import { ColumnsType } from "antd/es/table";
import { Pagination } from "types/general";
import { DateUtils } from "services/utils/DateUtils";
import {Customer} from "services/rest/general";
import { useIntl } from "react-intl";

export type LicenseKeysToDeleteModalProps = {
  visible: boolean;
  onOk: (keys: LicenseKeyRow[]) => void;
  onCancel: () => void;
  subscriptionId?: string;
  licenseKeysCount?: number;
};

export type LicenseKeyRow = {
  key: string;
  id: string;
  licenseKey: string;
  customer: Customer;
  startedOn: string;
  expireOn: string;
};

export function LicenseKeysToDeleteModal(props: LicenseKeysToDeleteModalProps) {
  const { visible, subscriptionId, onOk, onCancel, licenseKeysCount } = props;
  const intl = useIntl();

  const columns: ColumnsType = [
    {
      title: intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.column.license-key" }),
      dataIndex: "licenseKey",
      key: "licenseKey",
    },
      {
      title: intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.column.assigned-to" }),
      render: (key: LicenseKey) => {
          if (!key.customer) {
              return (
                  <div className="license-keys-card__not-assigned">{intl.formatMessage({ id: "jmix-store.license-keys-card.not-assigned" })}</div>
              );
          }

          const customerName = key.customer.firstName+ " " + key.customer.lastName
          return (
              <div>
                  <div className="license-keys-card__email">{key.customer.email}</div>
                  <span className="license-keys-card__name">{customerName}</span>
              </div>
          );
      },
    },
    {
      title: intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.column.started-on" }),
      dataIndex: "startedOn",
      key: "startedOn",
    },
    {
      title: intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.column.expire-on" }),
      dataIndex: "expireOn",
      key: "expireOn",
    },
  ];

  const [licenseKeys, setLicenseKeys] = useState<LicenseKeyRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 10,
  });
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);

  if (!subscriptionId) {
    return <Modal />;
  }
  const { response } = useSubscriptionLicenseKeys(
    subscriptionId,
    pagination.page,
    pagination.size
  );

  const dataSource = response?.content
    .filter((key) => key.state != LicenseState.NON_RENEWING)
    .map((key) => {
      return {
        key: key.id,
        id: key.id,
        licenseKey: key.sid,
        customer: key.customer,
        startedOn: DateUtils.getFormatDate(key.startDate),
        expireOn: DateUtils.getFormatDate(key.endDate),
      };
    });

  return (
    <Modal
      centered
      destroyOnClose={true}
      width="800px"
      title={intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.title" })}
      open={visible}
      onOk={() => onOk(licenseKeys)}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onCancel();
          }}
        >
          {intl.formatMessage({ id: "jmix-store.modal.action.cancel" })}
        </Button>,
        <Button
          disabled={updateButtonDisabled}
          type={"primary"}
          key="update"
          onClick={() => {
            onOk(licenseKeys);
          }}
        >
          {intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.update-button" })}
        </Button>,
      ]}
      okText={intl.formatMessage({ id: "jmix-store.license-keys-to-delete-modal.update-button" })}
    >
      <StoreTable
        pagination={{
          total: response?.total,
          hideOnSinglePage: true,
          showSizeChanger: false,
          onChange: (page) => setPagination({ page: page, size: pagination.size }),
        }}
        rowSelection={{
          type: "checkbox",
          onChange: (
            selectedRowKeys: React.Key[],
            selectedRows: LicenseKeyRow[]
          ) => {
            if (selectedRowKeys.length === licenseKeysCount) {
              setUpdateButtonDisabled(false);
            } else {
              setUpdateButtonDisabled(true);
            }
            setLicenseKeys(selectedRows);
          },
        }}
        columns={columns}
        dataSource={dataSource}
      />
    </Modal>
  );
}
